import React from 'react'
import { 
  Building2, 
  User, 
  MapPin, 
  DollarSign, 
  Users, 
  CheckCircle2,
  TrendingUp,
  Target,
  Globe,
  Mail,
  Phone,
  Building,
  Calendar,
  Briefcase,
  MapIcon,
  Lightbulb
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { getIndustryConfig } from '../industry-configs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Input } from '../../../components/ui/Input'
import { Label } from '../../../components/ui/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select'
import { Badge } from '../../../components/ui/Badge'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { cn } from '../../../lib/utils'

export const BusinessProfileScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()
  
  if (!data.selectedIndustry) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Industry Selection Required</h3>
          <p className="text-gray-500">Please select an industry first to continue with your business profile setup.</p>
        </div>
      </div>
    )
  }
  
  const industryConfig = getIndustryConfig(data.selectedIndustry)

  const handleContactInfoChange = (field: string, value: string) => {
    updateData('contactInfo', {
      ...data.contactInfo,
      [field]: value
    })
  }

  const handleBusinessProfileChange = (field: string, value: string) => {
    updateData('businessProfile', {
      ...data.businessProfile,
      [field]: value
    })
  }

  const handleLocationChange = (field: string, value: string) => {
    updateData('businessProfile', {
      ...data.businessProfile,
      location: {
        ...data.businessProfile.location,
        [field]: value
      }
    })
  }

  const annualRevenueRanges = [
    { value: 'under-500k', label: 'Under $500K', description: 'Small business operations' },
    { value: '500k-1m', label: '$500K - $1M', description: 'Growing business' },
    { value: '1m-2m', label: '$1M - $2M', description: 'Established business' },
    { value: '2m-5m', label: '$2M - $5M', description: 'Mid-market business' },
    { value: '5m-10m', label: '$5M - $10M', description: 'Large business' },
    { value: '10m-plus', label: '$10M+', description: 'Enterprise business' }
  ]

  const companySizes = [
    { value: '1-10', label: '1-10 employees', description: 'Small team' },
    { value: '11-25', label: '11-25 employees', description: 'Growing team' },
    { value: '26-50', label: '26-50 employees', description: 'Medium team' },
    { value: '51-100', label: '51-100 employees', description: 'Large team' },
    { value: '100-plus', label: '100+ employees', description: 'Enterprise team' }
  ]

  const serviceRadiusOptions = [
    { value: '5-miles', label: '5 miles', description: 'Local neighborhood' },
    { value: '10-miles', label: '10 miles', description: 'City area' },
    { value: '25-miles', label: '25 miles', description: 'Metro area' },
    { value: '50-miles', label: '50 miles', description: 'Regional area' },
    { value: 'regional', label: 'Regional (100+ miles)', description: 'Multi-city coverage' },
    { value: 'national', label: 'National', description: 'Nationwide service' }
  ]

  // Get selected business type details
  const selectedBusinessType = industryConfig.businessTypes.find(
    t => t.id === data.businessProfile.businessType
  )

  // Form completion progress
  const completedFields = [
    data.contactInfo.companyName,
    data.contactInfo.contactPerson,
    data.contactInfo.email,
    data.businessProfile.businessType,
    data.businessProfile.location.city,
    data.businessProfile.location.state
  ].filter(Boolean).length

  const totalRequiredFields = 6
  const completionPercentage = Math.round((completedFields / totalRequiredFields) * 100)

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-primary/10 rounded-full">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-primary">{industryConfig.name}</span>
        </div>
        
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Business Profile Setup
          <HelpTooltip text="Provide key business details to customize your intelligence reports. This information helps us deliver insights specific to your company size, location, and market position." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Help us understand your business so we can provide targeted intelligence 
          that matters to your specific situation and market position.
        </p>

        {/* Progress Indicator */}
        <div className="mt-8 max-w-md mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Profile Completion</span>
            <span className="text-sm font-bold text-primary">{completionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-primary to-primary/80 h-2 rounded-full transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Company Information */}
        <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
              Company Information
            </CardTitle>
            <CardDescription className="text-base">
              Essential details about your business operations and structure
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="company-name" className="flex items-center gap-2 font-medium">
                <Building className="w-4 h-4 text-gray-500" />
                Company Name *
              </Label>
              <Input
                id="company-name"
                value={data.contactInfo.companyName}
                onChange={(e) => handleContactInfoChange('companyName', e.target.value)}
                placeholder="Enter your company name"
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="business-type" className="flex items-center gap-2 font-medium">
                <Briefcase className="w-4 h-4 text-gray-500" />
                Business Type *
              </Label>
              <Select 
                value={data.businessProfile.businessType} 
                onValueChange={(value) => handleBusinessProfileChange('businessType', value)}
              >
                <SelectTrigger className="h-12 text-base">
                  <SelectValue placeholder="Select your business type" />
                </SelectTrigger>
                <SelectContent>
                  {industryConfig.businessTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id} className="py-3">
                      <div className="space-y-1">
                        <div className="font-medium">{type.name}</div>
                        <div className="text-sm text-gray-500">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedBusinessType && (
                <div className="mt-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-blue-800 mb-1">Intelligence Customization</div>
                      <div className="text-sm text-blue-700">
                        As a <strong>{selectedBusinessType.name.toLowerCase()}</strong>, we'll customize 
                        your intelligence monitoring for the specific challenges and opportunities 
                        in your market segment, including competitor analysis and industry-specific trends.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="annual-revenue" className="flex items-center gap-2 font-medium">
                  <DollarSign className="w-4 h-4 text-gray-500" />
                  Annual Revenue
                </Label>
                <Select 
                  value={data.businessProfile.annualRevenue} 
                  onValueChange={(value) => handleBusinessProfileChange('annualRevenue', value)}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {annualRevenueRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value} className="py-3">
                        <div className="space-y-1">
                          <div className="font-medium">{range.label}</div>
                          <div className="text-sm text-gray-500">{range.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company-size" className="flex items-center gap-2 font-medium">
                  <Users className="w-4 h-4 text-gray-500" />
                  Company Size
                </Label>
                <Select 
                  value={data.businessProfile.companySize} 
                  onValueChange={(value) => handleBusinessProfileChange('companySize', value)}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {companySizes.map((size) => (
                      <SelectItem key={size.value} value={size.value} className="py-3">
                        <div className="space-y-1">
                          <div className="font-medium">{size.label}</div>
                          <div className="text-sm text-gray-500">{size.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              Contact Information
            </CardTitle>
            <CardDescription className="text-base">
              Primary contact details for your intelligence delivery and account management
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="contact-person" className="flex items-center gap-2 font-medium">
                <User className="w-4 h-4 text-gray-500" />
                Primary Contact *
              </Label>
              <Input
                id="contact-person"
                value={data.contactInfo.contactPerson}
                onChange={(e) => handleContactInfoChange('contactPerson', e.target.value)}
                placeholder="Full name"
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 font-medium">
                <Mail className="w-4 h-4 text-gray-500" />
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={data.contactInfo.email}
                onChange={(e) => handleContactInfoChange('email', e.target.value)}
                placeholder="contact@company.com"
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2 font-medium">
                <Phone className="w-4 h-4 text-gray-500" />
                Phone Number
              </Label>
              <Input
                id="phone"
                type="tel"
                value={data.contactInfo.phone || ''}
                onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="h-12 text-base"
              />
            </div>

            {/* Contact Info Preview */}
            {data.contactInfo.contactPerson && data.contactInfo.email && (
              <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-green-800 mb-1">Contact Verified</div>
                    <div className="text-sm text-green-700">
                      Intelligence reports and alerts will be delivered to <strong>{data.contactInfo.contactPerson}</strong> at <strong>{data.contactInfo.email}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Service Area - Full Width */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            Service Area & Geographic Focus
          </CardTitle>
          <CardDescription className="text-base">
            Define your geographic market for localized intelligence monitoring and competitive analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-2">
              <Label htmlFor="city" className="flex items-center gap-2 font-medium">
                <Building className="w-4 h-4 text-gray-500" />
                City *
              </Label>
              <Input
                id="city"
                value={data.businessProfile.location.city}
                onChange={(e) => handleLocationChange('city', e.target.value)}
                placeholder="Enter city"
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="state" className="flex items-center gap-2 font-medium">
                <MapIcon className="w-4 h-4 text-gray-500" />
                State *
              </Label>
              <Input
                id="state"
                value={data.businessProfile.location.state}
                onChange={(e) => handleLocationChange('state', e.target.value)}
                placeholder="State"
                maxLength={2}
                className="h-12 text-base uppercase"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="zip-code" className="flex items-center gap-2 font-medium">
                <Target className="w-4 h-4 text-gray-500" />
                ZIP Code
              </Label>
              <Input
                id="zip-code"
                value={data.businessProfile.location.zipCode}
                onChange={(e) => handleLocationChange('zipCode', e.target.value)}
                placeholder="12345"
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="service-radius" className="flex items-center gap-2 font-medium">
                <Globe className="w-4 h-4 text-gray-500" />
                Service Radius
              </Label>
              <Select 
                value={data.businessProfile.location.serviceRadius} 
                onValueChange={(value) => handleLocationChange('serviceRadius', value)}
              >
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select radius" />
                </SelectTrigger>
                <SelectContent>
                  {serviceRadiusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="py-3">
                      <div className="space-y-1">
                        <div className="font-medium">{option.label}</div>
                        <div className="text-sm text-gray-500">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {data.businessProfile.location.city && data.businessProfile.location.state && (
            <div className="mt-6 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-purple-800 mb-2 text-lg">Local Market Intelligence Activated</div>
                  <div className="text-purple-700 space-y-2">
                    <p>
                      We'll monitor local market activity in <strong>{data.businessProfile.location.city}, {data.businessProfile.location.state}</strong> including:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                        <span className="text-sm">New competitor locations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                        <span className="text-sm">Facility expansions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                        <span className="text-sm">Equipment investments</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                        <span className="text-sm">Local pricing changes</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Summary */}
      {completionPercentage >= 80 && (
        <div className="mt-8 p-6 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-emerald-800 mb-2">Profile Setup Nearly Complete!</h3>
              <p className="text-emerald-700 mb-4">
                Your business profile is {completionPercentage}% complete. You're ready to proceed to the next step where we'll configure your revenue intelligence monitoring.
              </p>
              <div className="text-sm text-emerald-600">
                🚀 Continue to set up intelligence monitoring tailored to your business needs
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}